/**
 * UsagePage.jsx — Client Admin Usage Dashboard
 *
 * Shows query volume, top modules, top users, and a 30-day trend chart.
 * Accessible to admin role only (enforced via Sidebar link + ProtectedRoute guard).
 * Calls GET /users/usage/summary — JWT-scoped to the caller's tenant.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import {
  MessageSquare, Users, Activity, TrendingUp, ArrowLeft, RefreshCw,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { getUsageSummary } from '../utils/api';

// ─── Component ────────────────────────────────────────────────────────────────

export default function UsagePage() {
  const { token, user } = useAuth();
  const navigate        = useNavigate();

  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');

  async function load() {
    setLoading(true);
    setError('');
    try {
      const result = await getUsageSummary(token);
      setData(result);
    } catch (err) {
      setError(err.message || 'Failed to load usage data');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Access guard ───────────────────────────────────────────────────────────
  if (user?.role !== 'admin') {
    return (
      <div style={{ padding: 40, color: 'var(--clr-error)' }}>
        Access denied — this page is for administrators only.
      </div>
    );
  }

  return (
    <div style={{ padding: '32px 40px', maxWidth: 1100, margin: '0 auto' }}>

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 28 }}>
        <button
          onClick={() => navigate('/')}
          style={{
            background: 'none', border: 'none', color: 'var(--clr-text-secondary)',
            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, padding: '4px 0',
          }}
        >
          <ArrowLeft size={16} /> Back to Chat
        </button>
        <div style={{ flex: 1 }} />
        <button
          onClick={load}
          disabled={loading}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            background: 'var(--clr-bg-elevated)', border: '1px solid var(--clr-border)',
            borderRadius: 8, padding: '6px 14px', color: 'var(--clr-text-secondary)',
            cursor: 'pointer', fontSize: '0.85rem',
          }}
        >
          <RefreshCw size={14} className={loading ? 'spin' : ''} />
          Refresh
        </button>
      </div>

      <h1 style={{
        fontFamily: 'var(--font-display)', fontSize: '1.5rem', fontWeight: 700,
        color: 'var(--clr-text-primary)', marginBottom: 6,
      }}>
        Usage Dashboard
      </h1>
      <p style={{ color: 'var(--clr-text-secondary)', marginBottom: 28, fontSize: '0.875rem' }}>
        Query analytics for your tenant · data is live
      </p>

      {/* ── Loading / Error ── */}
      {loading && !data && (
        <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--clr-text-muted)' }}>
          Loading usage data…
        </div>
      )}
      {error && (
        <div style={{ padding: 16, background: 'var(--clr-error-bg)', color: 'var(--clr-error)', borderRadius: 10 }}>
          {error}
        </div>
      )}

      {data && (
        <>
          {/* ── 3 Stat cards ── */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 28 }}>
            <StatCard
              icon={<MessageSquare size={18} style={{ color: 'var(--clr-accent)' }} />}
              label="Queries Today"
              value={data.total_queries_today}
            />
            <StatCard
              icon={<Activity size={18} style={{ color: '#10B981' }} />}
              label="Queries This Month"
              value={data.total_queries_this_month}
            />
            <StatCard
              icon={<TrendingUp size={18} style={{ color: '#F59E0B' }} />}
              label="Queries All Time"
              value={data.total_queries_all_time}
            />
          </div>

          {/* ── Charts row ── */}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20, marginBottom: 24 }}>

            {/* 30-day bar chart */}
            <div style={{
              background: 'var(--clr-bg-surface)', border: '1px solid var(--clr-border)',
              borderRadius: 12, padding: '20px 24px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                <TrendingUp size={16} style={{ color: 'var(--clr-accent)' }} />
                <span style={{ fontWeight: 600, color: 'var(--clr-text-primary)', fontSize: '0.9rem' }}>
                  Queries — Last 30 Days
                </span>
              </div>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={data.queries_last_30_days} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--clr-border-soft)" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10, fill: 'var(--clr-text-muted)' }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(d) => {
                      // Parse "YYYY-MM-DD" directly to avoid UTC/local timezone offset
                      const day = parseInt(d.split('-')[2], 10);
                      return day % 7 === 1 ? String(day) : '';
                    }}
                  />
                  <YAxis tick={{ fontSize: 10, fill: 'var(--clr-text-muted)' }} tickLine={false} axisLine={false} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{
                      background: 'var(--clr-bg-elevated)',
                      border: '1px solid var(--clr-border)',
                      borderRadius: 8,
                      fontSize: 12,
                      color: 'var(--clr-text-primary)',
                    }}
                    labelFormatter={(label) => {
                      // Parse "YYYY-MM-DD" directly — avoids UTC/timezone shift
                      const [y, m, d] = label.split('-').map(Number);
                      return new Date(y, m - 1, d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
                    }}
                    formatter={(val) => [val, 'Queries']}
                  />
                  <Bar dataKey="count" fill="var(--clr-accent)" radius={[3,3,0,0]} maxBarSize={18} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Top modules */}
            <div style={{
              background: 'var(--clr-bg-surface)', border: '1px solid var(--clr-border)',
              borderRadius: 12, padding: '20px 24px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                <Activity size={16} style={{ color: '#10B981' }} />
                <span style={{ fontWeight: 600, color: 'var(--clr-text-primary)', fontSize: '0.9rem' }}>
                  Top Modules
                </span>
              </div>
              {data.top_modules.length === 0 ? (
                <p style={{ color: 'var(--clr-text-muted)', fontSize: '0.85rem' }}>No data yet</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {data.top_modules.map((m, i) => {
                    const max = data.top_modules[0]?.count || 1;
                    const pct = Math.round((m.count / max) * 100);
                    return (
                      <div key={m.module}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                          <span style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--clr-text-primary)' }}>{m.module}</span>
                          <span style={{ fontSize: '0.78rem', color: 'var(--clr-text-muted)' }}>{m.count}</span>
                        </div>
                        <div style={{ height: 6, borderRadius: 3, background: 'var(--clr-bg-elevated)', overflow: 'hidden' }}>
                          <div style={{
                            height: '100%', borderRadius: 3,
                            width: `${pct}%`,
                            background: ['var(--clr-accent)', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'][i % 5],
                          }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--clr-border-soft)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <Users size={14} style={{ color: 'var(--clr-text-muted)' }} />
                  <span style={{ fontSize: '0.8rem', color: 'var(--clr-text-secondary)', fontWeight: 600 }}>
                    Active Users This Month
                  </span>
                </div>
                <span style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--clr-text-primary)' }}>
                  {data.active_users_this_month}
                </span>
              </div>
            </div>
          </div>

          {/* ── Top users table ── */}
          <div style={{
            background: 'var(--clr-bg-surface)', border: '1px solid var(--clr-border)',
            borderRadius: 12, padding: '20px 24px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              <Users size={16} style={{ color: '#8B5CF6' }} />
              <span style={{ fontWeight: 600, color: 'var(--clr-text-primary)', fontSize: '0.9rem' }}>
                Top 5 Users by Query Count
              </span>
            </div>
            {data.top_users.length === 0 ? (
              <p style={{ color: 'var(--clr-text-muted)', fontSize: '0.85rem' }}>No queries yet</p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', color: 'var(--clr-text-secondary)', fontWeight: 600, padding: '6px 0', borderBottom: '1px solid var(--clr-border)' }}>#</th>
                    <th style={{ textAlign: 'left', color: 'var(--clr-text-secondary)', fontWeight: 600, padding: '6px 0', borderBottom: '1px solid var(--clr-border)' }}>User</th>
                    <th style={{ textAlign: 'right', color: 'var(--clr-text-secondary)', fontWeight: 600, padding: '6px 0', borderBottom: '1px solid var(--clr-border)' }}>Queries</th>
                  </tr>
                </thead>
                <tbody>
                  {data.top_users.map((u, i) => (
                    <tr key={u.email}>
                      <td style={{ padding: '8px 0', color: 'var(--clr-text-muted)', width: 32 }}>{i + 1}</td>
                      <td style={{ padding: '8px 0', color: 'var(--clr-text-primary)' }}>{u.email}</td>
                      <td style={{ padding: '8px 0', textAlign: 'right', fontWeight: 600, color: 'var(--clr-accent-light)' }}>{u.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}


// ─── Stat Card ────────────────────────────────────────────────────────────────

function StatCard({ icon, label, value }) {
  return (
    <div style={{
      background: 'var(--clr-bg-surface)', border: '1px solid var(--clr-border)',
      borderRadius: 12, padding: '20px 24px',
      display: 'flex', alignItems: 'flex-start', gap: 14,
    }}>
      <div style={{
        width: 36, height: 36, borderRadius: 8,
        background: 'var(--clr-bg-elevated)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        {icon}
      </div>
      <div>
        <div style={{ fontSize: '1.75rem', fontWeight: 700, color: 'var(--clr-text-primary)', lineHeight: 1.1 }}>
          {value?.toLocaleString() ?? '—'}
        </div>
        <div style={{ fontSize: '0.8rem', color: 'var(--clr-text-secondary)', marginTop: 4 }}>{label}</div>
      </div>
    </div>
  );
}
